"use client";

import { useStytchMember } from "@stytch/nextjs/b2b";
import { createContext, useContext, type ReactNode } from "react";

import { useGroupContext } from "@/src/lib/data-fetchers/group-fetchers";
import type { Group } from "@/src/models/group";

interface UserContextProps {
  email: string | null;
  stytchOrgId: string | null;
  group: Group | null;
  groups: Group[];
  tenancy: string | null;
  roles: string[];
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

const EMPTY_USER_CONTEXT: UserContextProps = {
  email: null,
  stytchOrgId: null,
  group: null,
  groups: [],
  tenancy: null,
  roles: [],
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { member } = useStytchMember();
  const { data: groupContext } = useGroupContext(member?.organization_id);
  const group = groupContext?.group ?? null;
  const isReady = Boolean(member && group);
  const userState: UserContextProps = {
    email: isReady ? (member?.email_address ?? null) : null,
    stytchOrgId: isReady ? (member?.organization_id ?? null) : null,
    group,
    groups: groupContext?.groups ?? [],
    tenancy: group?.tenancy ?? null,
    roles: isReady ? (member?.roles?.map((role) => role.role_id) ?? []) : [],
  };

  return <UserContext.Provider value={userState}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};
